import { create } from '@bufbuild/protobuf';
import useCloudService from '@grpc/composables/useCloudService';
import {
  AuthenticationType,
  type BasicImage,
  type OperatingSystem,
} from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import {
  CreateProjectImageRequestSchema,
  DeleteProjectImageRequestSchema,
  DeleteProjectImageVersionRequestSchema,
  GetProjectImageRequestSchema,
  ListProjectImagesRequestSchema,
  ListPublicImagesRequestSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import {
  type CreateProjectImageResponse,
  type GetProjectImageResponse,
  type ListProjectImagesResponse,
  type ListPublicImagesResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';

type ImageStoreState = {
  publicImages: Array<OperatingSystem>;
  projectImages: Array<BasicImage>;
};

export const useImageStore = defineStore('image', () => {
  const cloudService = useCloudService();

  const state = reactive<ImageStoreState>({
    publicImages: new Array<OperatingSystem>(),
    projectImages: new Array<BasicImage>(),
  });

  const reloadPublicImages = async (
    providerType?: CloudProviderType,
  ): Promise<ListPublicImagesResponse> => {
    const request = create(ListPublicImagesRequestSchema);
    if (providerType != null && providerType !== CloudProviderType.UNSPECIFIED) {
      request.cloudProviderType = providerType;
    }
    const { response, error } = await cloudService.executeApiCall('listPublicImages', request);
    if (error != null) throw error;

    state.publicImages = response.operatingSystems;

    return response;
  };

  const reloadProjectImages = async (
    projectId: string,
    onlyAvailable: boolean = false,
  ): Promise<ListProjectImagesResponse> => {
    const request = create(ListProjectImagesRequestSchema);
    request.id = projectId;
    if (onlyAvailable) {
      request.onlyAvailable = onlyAvailable;
    }

    const { response, error } = await cloudService.executeApiCall('listProjectImages', request);
    if (error != null) throw error;

    state.projectImages = response.images;

    return response;
  };

  const getProjectImage = async (
    projectId: string,
    imageId: string,
  ): Promise<GetProjectImageResponse> => {
    const request = create(GetProjectImageRequestSchema);
    request.projectId = projectId;
    request.id = imageId;

    const { response, error } = await cloudService.executeApiCall('getProjectImage', request);
    if (error != null) throw error;

    return response;
  };

  const deleteImageVersion = async (projectId: string, versionId: string): Promise<void> => {
    const request = create(DeleteProjectImageVersionRequestSchema);
    request.projectId = projectId;
    request.id = versionId;

    const { error } = await cloudService.executeApiCall('deleteProjectImageVersion', request);
    if (error != null) throw error;
  };

  const createProjectImage = async (
    projectId: string,
    {
      name,
      authenticationTypes,
    }: {
      name: string;
      authenticationTypes: Array<AuthenticationType>;
    },
  ): Promise<CreateProjectImageResponse> => {
    const request = create(CreateProjectImageRequestSchema);
    request.id = projectId;
    request.name = name;
    request.authenticationTypes = authenticationTypes;

    const { response, error } = await cloudService.executeApiCall('createProjectImage', request);
    if (error != null) throw error;

    return response;
  };

  const deleteProjectImage = async (projectId: string, imageId: string): Promise<void> => {
    const request = create(DeleteProjectImageRequestSchema);
    request.projectId = projectId;
    request.id = imageId;

    const { error } = await cloudService.executeApiCall('deleteProjectImage', request);
    if (error != null) throw error;

    state.projectImages = state.projectImages.filter((image) => image.id !== imageId);
  };

  return {
    ...toRefs(state as ImageStoreState),
    reloadPublicImages,
    reloadProjectImages,
    createProjectImage,
    getProjectImage,
    deleteProjectImage,
    deleteImageVersion,
  };
});

export default useImageStore;
